/**
 * Live countdown for the cart reservation message.
 *
 * The deadline is anchored client-side in sessionStorage so it stays stable
 * across the theme's morph-based cart re-renders and across navigation in the
 * same browser tab.
 *
 * The deadline restarts every time a product is added to the cart (any
 * `form[action*="/cart/add"]` submit), so the countdown always measures the
 * time since the last "add to cart".
 *
 * The theme morphs cart markup on every update. `data-skip-node-update` only
 * protects a node's own attributes (children are still reconciled), and a
 * morphed node keeps its custom element instance, so `connectedCallback` does
 * NOT run again. The recurring tick below is what re-applies the live text and
 * the `data-expired` state after any external DOM update.
 */
const TICK_MS = 500;
const DEFAULT_KEY = 'cart-urgency-deadline';

/** @type {Set<CartUrgencyTimer>} */
const instances = new Set();

/** @type {Record<string, string>} */
const memoryFallback = {};

/**
 * @param {string} key
 * @param {number} minutes
 * @returns {number}
 */
function startDeadline(key, minutes) {
  const deadline = Date.now() + minutes * 60_000;
  const value = String(deadline);
  memoryFallback[key] = value;
  try {
    window.sessionStorage.setItem(key, value);
  } catch {
    // Ignore: the countdown still works for this page from memory.
  }
  return deadline;
}

class CartUrgencyTimer extends HTMLElement {
  /** @type {number | null} */
  #interval = null;

  /** @type {number | null} */
  #deadline = null;

  connectedCallback() {
    instances.add(this);
    if (this.#interval) return;
    this.#deadline = null;
    this.#update();
    this.#interval = window.setInterval(() => this.#update(), TICK_MS);
  }

  disconnectedCallback() {
    instances.delete(this);
    window.clearInterval(this.#interval);
    this.#interval = null;
  }

  get storageKey() {
    return this.dataset.storageKey || DEFAULT_KEY;
  }

  get minutes() {
    return Number(this.dataset.minutes) || 0;
  }

  /** Restart the countdown from now. */
  restart() {
    this.#deadline = startDeadline(this.storageKey, this.minutes);
    this.#update();
  }

  /** @returns {number} */
  #resolveDeadline() {
    if (this.#deadline !== null) return this.#deadline;

    const key = this.storageKey;

    let stored = memoryFallback[key] ?? null;
    try {
      stored = stored ?? window.sessionStorage.getItem(key);
    } catch {
      // Session storage can be unavailable (private mode): the in-memory fallback is enough.
    }

    let deadline = Number(stored);
    if (!deadline || Number.isNaN(deadline)) {
      deadline = startDeadline(key, this.minutes);
    }

    this.#deadline = deadline;
    return deadline;
  }

  #update() {
    const output = this.querySelector('[data-cart-urgency-time]');
    if (!output) return;

    const remaining = this.#resolveDeadline() - Date.now();

    if (remaining <= 0) {
      this.toggleAttribute('data-expired', true);
      if (output.textContent !== '00:00') output.textContent = '00:00';
      return;
    }

    this.toggleAttribute('data-expired', false);
    this.toggleAttribute('data-hurry', remaining <= 60_000);

    const totalSeconds = Math.floor(remaining / 1000);
    const next = `${String(Math.floor(totalSeconds / 60)).padStart(2, '0')}:${String(
      totalSeconds % 60
    ).padStart(2, '0')}`;

    if (output.textContent !== next) output.textContent = next;
  }
}

// Restart the countdown whenever a product is added to the cart.
document.addEventListener(
  'submit',
  (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    const action = form.getAttribute('action') || '';
    if (!action.includes('/cart/add')) return;

    const minutesAttr = document.querySelector('cart-urgency-timer')?.dataset.minutes;
    const minutes = Number(minutesAttr) || Number(document.documentElement.dataset.cartUrgencyMinutes) || 5;

    startDeadline(DEFAULT_KEY, minutes);
    instances.forEach((timer) => timer.restart());
  },
  true
);

if (!customElements.get('cart-urgency-timer')) {
  customElements.define('cart-urgency-timer', CartUrgencyTimer);
}
